import React, { createContext, useContext, useState } from "react";

const StateContext = createContext();

export const useStateContext = () => {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error("useStateContext must be used within a StateProvider");
  }
  return context;
};

export const StateProvider = ({ children }) => {
  const [state, setState] = useState({
    sortByTime: [{ id: 1, title: "Recently Posted" }],
    sortByExperience: [],
    pageNumber: 0,
    sortlanguage: "",
    sortposttype: [],
    sortexperttype: [],
    locationType: [],
    industries: [],
    countries: [],
    hiringType: [],
    searchlocation: "",
    location: "",
    remoteChecked: false,
    onSiteChecked: false,
    keywords: [],
    specialities: [],
    hiringtype: [],
    experienceLevel: "",
    subcategories: [],
    minHourlyRate: "",
    maxHourlyRate: "",
    minRates: {},
    maxRates: {},
    proposed: "",
    viewed: "",
    wishlist: "",
    rerender: "",
    searchexpert: "",
    search: "",
    allpostfiltertab: "",
  });

  const setStateAsync = (newState) =>
    new Promise((resolve) => {
      setState((prevState) => {
        const updatedState = { ...prevState, ...newState };
        resolve(updatedState);
        return updatedState;
      });
    });

  const clearAllFields = async () => {
    await setStateAsync({
      sortByTime: [{ id: 1, title: "Recently Posted" }],
      sortByExperience: [],
      pageNumber: 0,
      sortlanguage: "",
      sortposttype: [],
      sortexperttype: [],
      locationType: [],
      hiringType: [],
      industries: [],
      countries: [],
      subcategories: [],
      searchlocation: "",
      location: "",
      remoteChecked: false,
      onSiteChecked: false,
      keywords: [],
      specialities: [],
      hiringtype: [],
      experienceLevel: "",
      minHourlyRate: "",
      maxHourlyRate: "",
      minRates: {},
      maxRates: {},
      searchexpert: "",
      search: "",
      allpostfiltertab: "",
    });
  };

  const locationTypeChange = async (type) => {
    const updatedTypes = state.locationType.includes(type)
      ? state.locationType.filter((t) => t !== type)
      : [...state.locationType, type];
    await setStateAsync({ pageNumber: 0, locationType: updatedTypes });
  };

  const hiringTypeChange = async (type) => {
    const updatedTypes = state.hiringType.includes(type)
      ? state.hiringType.filter((t) => t !== type)
      : [...state.hiringType, type];
    await setStateAsync({ pageNumber: 0, hiringType: updatedTypes });
  };

  const setAllpostfiltertab = async (value) =>
    await setStateAsync({ pageNumber: 0, allpostfiltertab: value });
  const setSortByTime = async (value) =>
    await setStateAsync({ pageNumber: 0, sortByTime: value });
  const setSortByExperience = async (value) =>
    await setStateAsync({ pageNumber: 0, sortByExperience: value });
  const setSortLanguage = async (value) =>
    await setStateAsync({ pageNumber: 0, sortlanguage: value });
  const setSortPostType = async (type) => {
    await setStateAsync({ pageNumber: 0, sortposttype: [type] });
  };

  const setSortExpertType = async (type) => {
    const updatedTypes = state.sortexperttype.some((t) => t.id == type.id)
      ? state.sortexperttype.filter((t) => t.id !== type.id)
      : [...state.sortexperttype, type];
    await setStateAsync({ pageNumber: 0, sortexperttype: updatedTypes });
  };

  const setSearchexpert = async (value) =>
    await setStateAsync({ pageNumber: 0, searchexpert: value });
  const setLocation = async (value) =>
    await setStateAsync({ pageNumber: 0, location: value });
  const setSearchlocation = async (value) =>
    await setStateAsync({ pageNumber: 0, searchlocation: value });
  const setRemoteChecked = async (value) =>
    await setStateAsync({ pageNumber: 0, remoteChecked: value });
  const setOnSiteChecked = async (value) =>
    await setStateAsync({ pageNumber: 0, onSiteChecked: value });
  const setKeywords = async (value) =>
    await setStateAsync({ pageNumber: 0, keywords: value });
  const setSpecialities = async (value) =>
    await setStateAsync({ pageNumber: 0, specialities: value });
  const setHiringtype = (value) => {
    setState((prev) => {
      const exists = prev.hiringtype?.some((item) => item.id == value.id);
      return {
        ...prev,
        hiringtype: exists
          ? prev.hiringtype.filter((item) => item.id != value.id) // REMOVE if already selected
          : [...(prev.hiringtype || []), value], // ADD if not selected
      };
    });
  };

  const setExperienceLevel = async (value) =>
    await setStateAsync({ pageNumber: 0, experienceLevel: value });
  const setMinHourlyRate = async (value) =>
    await setStateAsync({ pageNumber: 0, minHourlyRate: value });
  const setMaxHourlyRate = async (value) =>
    await setStateAsync({ pageNumber: 0, maxHourlyRate: value });
  const setMinRateById = async (id, value) => {
    await setStateAsync({ pageNumber: 0, minRates: { ...state.minRates, [id]: value } });
  };
  const setMaxRateById = async (id, value) => {
    await setStateAsync({ pageNumber: 0, maxRates: { ...state.maxRates, [id]: value } });
  };
  const setPageNumber = async (value) =>
    await setStateAsync({ pageNumber: value });
  const setIndustries = async (value) =>
    await setStateAsync({ pageNumber: 0, industries: value });
  const setCountries = async (value) =>
    await setStateAsync({ pageNumber: 0, countries: value });
  const setSubcategories = async (value) =>
    await setStateAsync({ pageNumber: 0, subcategories: value });
  const setProposed = async (value) =>
    await setStateAsync({
      pageNumber: 0,
      proposed: 1,
      viewed: "",
      wishlist: "",
    });
  const setViewed = async (value) =>
    await setStateAsync({
      pageNumber: 0,
      proposed: "",
      viewed: 1,
      wishlist: "",
    });
  const setWishlist = async (value) =>
    await setStateAsync({
      pageNumber: 0,
      proposed: "",
      viewed: "",
      wishlist: 1,
    });
  const setListNull = async (value) =>
    await setStateAsync({
      pageNumber: 0,
      proposed: "",
      viewed: "",
      wishlist: "",
    });
  const setRerender = async (value) => await setStateAsync({ rerender: 1 });
  const typofpost = state?.sortposttype?.filter((value) => true);
  const setSearch = async (value) =>
    await setStateAsync({ pageNumber: 0, search: value });

  return (
    <StateContext.Provider
      value={{
        setState,
        setAllpostfiltertab,
        state,
        typofpost,
        setSortByTime,
        setSortByExperience,
        setSortLanguage,
        setSortPostType,
        setSortExpertType,
        setSearchexpert,
        hiringTypeChange,
        locationTypeChange,
        setSearchlocation,
        setLocation,
        setRemoteChecked,
        setIndustries,
        setCountries,
        setSubcategories,
        setOnSiteChecked,
        setKeywords,
        setSpecialities,
        setHiringtype,
        setExperienceLevel,
        setMinHourlyRate,
        setMaxHourlyRate,
        setMinRateById,
        setMaxRateById,
        clearAllFields,
        setPageNumber,
        setProposed,
        setViewed,
        setWishlist,
        setListNull,
        setRerender,
        setSearch,
      }}
    >
      {children}
    </StateContext.Provider>
  );
};
